import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ZoneModal from './ZoneModal';
import {
  HiOutlineOfficeBuilding,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineClock,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineViewGrid
} from 'react-icons/hi';
import { TbBuildingFactory2, TbSunrise, TbDoorEnter } from 'react-icons/tb';
import { FiSun } from 'react-icons/fi';
import { IoFlashOutline } from 'react-icons/io5';
import { HiOutlineLightBulb } from 'react-icons/hi';

const API_URL = '/api';

function ZonesPage({ token }) {
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState('');
  const [zones, setZones] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingZone, setEditingZone] = useState(null);

  useEffect(() => {
    loadFacilities();
  }, []);

  useEffect(() => {
    if (selectedFacility) {
      loadZones();
    }
  }, [selectedFacility]);

  const loadFacilities = async () => {
    try {
      const response = await axios.get(`${API_URL}/facilities`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFacilities(response.data);
      if (response.data.length > 0) {
        setSelectedFacility(response.data[0]._id);
      }
    } catch (err) {
      console.error('Tesisler yüklenemedi:', err);
    }
  };

  const loadZones = async () => {
    try {
      const response = await axios.get(`${API_URL}/zones?facility=${selectedFacility}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setZones(response.data);
    } catch (err) {
      console.error('Bölümler yüklenemedi:', err);
    }
  };

  const deleteZone = async (id) => {
    if (!window.confirm('Bu bölümü silmek istediğinizden emin misiniz?')) return;
    
    try {
      await axios.delete(`${API_URL}/zones/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Bölüm silindi');
      loadZones();
    } catch (err) {
      alert('Hata: ' + err.response?.data?.error);
    }
  };

  const zoneTypeLabels = {
    'hol_ic': { icon: HiOutlineOfficeBuilding, label: 'HOL İÇ', color: '#3B82F6' },
    'hol_dis': { icon: TbDoorEnter, label: 'HOL DIŞ', color: '#10B981' },
    'cevre_aydinlatma': { icon: HiOutlineLightBulb, label: 'ÇEVRE AYDINLATMA', color: '#F59E0B' }
  };

  return (
    <div className="admin-page">
      <div className="card">
        <h3>
          <HiOutlineViewGrid style={{marginRight: '8px', verticalAlign: 'middle'}} />
          Bölüm Yönetimi
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-add"
            disabled={!selectedFacility}
          >
            + Yeni Bölüm
          </button>
        </h3>

        {facilities.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <TbBuildingFactory2 size={48} color="#666" />
            </div>
            <div className="empty-state-text">Önce tesis eklemelisiniz</div>
          </div>
        ) : (
          <>
            <div className="form-group" style={{marginTop: '20px'}}>
              <label>TESİS SEÇİN</label>
              <select
                value={selectedFacility}
                onChange={(e) => setSelectedFacility(e.target.value)}
                style={{maxWidth: '400px'}}
              >
                {facilities.map(f => (
                  <option key={f._id} value={f._id}>{f.facilityName}</option>
                ))}
              </select>
            </div>

            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Bölüm Adı</th>
                    <th>Tip</th>
                    <th>Açıklama</th>
                    <th>Otomasyon</th>
                    <th>Durum</th>
                    <th>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {zones.length === 0 ? (
                    <tr>
                      <td colSpan="6" style={{textAlign: 'center', padding: '40px', color: '#666'}}>
                        Bu tesiste henüz bölüm yok
                      </td>
                    </tr>
                  ) : (
                    zones.map(zone => {
                      const ZoneIcon = zoneTypeLabels[zone.zoneType]?.icon || HiOutlineOfficeBuilding;
                      const zoneColor = zoneTypeLabels[zone.zoneType]?.color || '#666';
                      return (
                        <tr key={zone._id}>
                          <td><strong>{zone.zoneName}</strong></td>
                          <td>
                            <span style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                              <ZoneIcon size={18} color={zoneColor} />
                              {zoneTypeLabels[zone.zoneType]?.label}
                            </span>
                          </td>
                          <td>{zone.description}</td>
                          <td>
                            {zone.automation?.enabled ? (
                              <span className="status-badge status-active" style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                                {zone.automation.scheduleMode === 'sunrise_sunset' ? (
                                  <><TbSunrise size={16} /> Gün Doğumu/Batımı</>
                                ) : zone.automation.scheduleMode === 'time' ? (
                                  <><HiOutlineClock size={16} /> {zone.automation.timeSchedule?.length || 0} Program</>
                                ) : 'Manuel'}
                              </span>
                            ) : (
                              <span className="status-badge status-inactive">Kapalı</span>
                            )}
                          </td>
                          <td>
                            <span className={`status-badge ${zone.isActive ? 'status-active' : 'status-inactive'}`} style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                              {zone.isActive ? <><HiOutlineCheckCircle size={16} /> Aktif</> : <><HiOutlineXCircle size={16} /> Pasif</>}
                            </span>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="btn-edit"
                                onClick={() => setEditingZone(zone)}
                                title="Düzenle"
                              >
                                <HiOutlinePencil size={16} />
                              </button>
                              <button
                                className="btn-delete"
                                onClick={() => deleteZone(zone._id)}
                                title="Sil"
                              >
                                <HiOutlineTrash size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {(showAddModal || editingZone) && (
        <ZoneModal
          zone={editingZone}
          facilityId={selectedFacility}
          token={token}
          onClose={() => {
            setShowAddModal(false);
            setEditingZone(null);
          }}
          onSuccess={() => {
            setShowAddModal(false);
            setEditingZone(null);
            loadZones();
          }}
        />
      )}
    </div>
  );
}

export default ZonesPage;
